import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { subscribeToCacheRefresh } from '../utils/cacheManager';

const ToursContext = createContext({ tours: [], loading: true });

export const useTours = () => useContext(ToursContext);

export const ToursProvider = ({ children }) => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTours = useCallback(async () => {
    try {
      // Cache versioning: bump this when tour schema or filtering logic changes
      const CACHE_VERSION = 'v2-event-only';
      const CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes (was 5) — shorter for admin changes

      // Check sessionStorage cache first
      const cached = sessionStorage.getItem('tours_cache');
      if (cached) {
        try {
          const { data, timestamp, version } = JSON.parse(cached);
          if (version === CACHE_VERSION && Date.now() - timestamp < CACHE_TTL_MS) {
            startTransition(() => { setTours(data); setLoading(false); });
            // Revalidate in background so admin changes propagate faster
            revalidateInBackground(CACHE_VERSION);
            return;
          }
        } catch (e) {
          // Corrupted cache, ignore and fetch fresh
        }
      }

      const response = await fetch(API_ENDPOINTS.tours + '?include_hidden=true', { cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        const toursData = data.tours || data || [];
        startTransition(() => setTours(toursData));
        sessionStorage.setItem('tours_cache', JSON.stringify({
          data: toursData,
          timestamp: Date.now(),
          version: CACHE_VERSION
        }));
      }
    } catch (error) {
      console.error('ToursContext: fetch error', error);
    } finally {
      startTransition(() => setLoading(false));
    }
  }, []);

  const revalidateInBackground = useCallback(async (version) => {
    try {
      const response = await fetch(API_ENDPOINTS.tours + '?include_hidden=true', { cache: 'no-store' });
      if (response.ok) {
        const data = await response.json();
        const toursData = data.tours || data || [];
        startTransition(() => setTours(toursData));
        sessionStorage.setItem('tours_cache', JSON.stringify({
          data: toursData,
          timestamp: Date.now(),
          version
        }));
      }
    } catch (e) { /* ignore background errors */ }
  }, []);

  useEffect(() => {
    // Delay initial fetch by 100ms to let LCP resources claim bandwidth first
    const timer = setTimeout(() => fetchTours(), 100);
    const unsubscribe = subscribeToCacheRefresh('tours', fetchTours);
    return () => { clearTimeout(timer); unsubscribe(); };
  }, [fetchTours]);

  const value = useMemo(() => ({ tours, loading, refetch: fetchTours }), [tours, loading, fetchTours]);

  return (
    <ToursContext.Provider value={value}>
      {children}
    </ToursContext.Provider>
  );
};
